import { usePrefetchQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'
import { GuestSaveBanner } from '@/components/GuestSaveBanner'
import { ModeSelect } from '@/components/ModeSelect'
import { RunForm } from '@/components/RunForm'
import type { RunFormValues } from '@/components/RunForm.types'
import { SeedInputReveal } from '@/components/SeedInputReveal'
import { useSubmitRun } from '@/lib/queries'
import { mergeSeedDraft } from '@/lib/seeds'
import { targetMarketCatalogOptions } from '@/lib/targetMarkets'
import type { Mode } from '@/lib/types'
import { useAnalytics, useAuth } from '@/provider/useAuth'
import { useFetcher } from '@/provider/useFetcher'
import { useSignupClick } from '@/provider/useSignupClick'
import { useTrackerNavigate } from '@/provider/useTrackerNavigate'

export default function Landing() {
  const fetcher = useFetcher()
  const navigate = useTrackerNavigate()

  usePrefetchQuery(targetMarketCatalogOptions(fetcher))
  const submit = useSubmitRun()
  const auth = useAuth()
  const analytics = useAnalytics()
  const { signupUrl, onSignupClick } = useSignupClick()

  const [mode, setMode] = useState<Mode>('direct')
  const [clusters, setClusters] = useState<string[]>([])
  const [clusterDraft, setClusterDraft] = useState('')

  // Gap Finder is a signed-in platform feature; anonymous users see it disabled.
  const intentMapAllowed = auth !== null

  function driveSignup() {
    onSignupClick('intent_map_mode')
    window.location.assign(signupUrl)
  }

  async function runAnalysis(values: RunFormValues) {
    // Fold in any uncommitted draft so typed-but-not-entered text is not dropped.
    const finalClusters =
      mode === 'intent_map' ? mergeSeedDraft(clusters, clusterDraft) : undefined
    const { targetMarketLabel, ...runInput } = values

    try {
      const result = await submit.mutateAsync({
        ...runInput,
        mode,
        clusters: finalClusters,
      })

      if (result) {
        // A 'complete' status straight off the submit means the backend served
        // a cached run (same brand/category/market within the freshness window);
        // 'pending' means a fresh run was enqueued.
        analytics.track('run_submit_clicked', {
          brand: values.brand,
          category: values.category,
          targetMarketIds: values.targetMarketIds,
          targetMarketLabel,
          cached: result.status === 'complete',
        })
        navigate(`/run/${result.runId}`)
      }
    } catch (err) {
      // ApiError carries the server's quota/auth message; a bare Error has none.
      toast.error(
        err instanceof Error ? err.message : 'Could not start the run.'
      )
    }
  }

  return (
    <main className="relative flex flex-1 flex-col items-center justify-center px-6 py-12">
      <div className="relative w-full max-w-2xl">
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
            See how AI talks
            <br />
            <span className="text-primary">about your brand.</span>
          </h1>
          <p className="mt-5 text-base text-muted-foreground max-w-lg mx-auto">
            We count how often ChatGPT, Claude, Gemini, Grok, and DeepSeek name
            your brand.
          </p>
        </div>

        <RunForm
          idPrefix="run"
          className="space-y-6"
          pending={submit.isPending}
          submitLabel="Run analysis"
          pendingLabel="Starting run…"
          helperText="We generate focused queries and check them across active AI models. This can take a couple of minutes."
          onSubmit={(values) => void runAnalysis(values)}
          modeControl={
            <ModeSelect
              name="mode"
              value={mode}
              gate={
                intentMapAllowed
                  ? undefined
                  : {
                      tooltip:
                        'Sign up to find where your brand is invisible across your category.',
                      onGatedClick: driveSignup,
                    }
              }
              onChange={setMode}
            />
          }
          runDetail={
            <SeedInputReveal
              show={intentMapAllowed && mode === 'intent_map'}
              id="run-clusters"
              values={clusters}
              draft={clusterDraft}
              helperText="Press Enter or comma to add each space. Leave blank to let the model discover them automatically."
              disabled={submit.isPending}
              onValuesChange={setClusters}
              onDraftChange={setClusterDraft}
            />
          }
        />

        {/* Guest banner only when anonymous — authed users' runs already save. */}
        {!auth && <GuestSaveBanner />}
      </div>
    </main>
  )
}
